import { Button } from "@/components/ui/buttons/Button";
import { Badge } from "@/components/ui/display/Badge";
import { FlashAlert } from "@/components/ui/feedback/FlashAlert";
import { StatusMessages } from "@/components/ui/feedback/StatusMessages";
import { FormField } from "@/components/ui/forms/FormField";
import { Select } from "@/components/ui/forms/Select";
import type { SelectOption } from "@/components/ui/forms/Select/types";
import { TextInput } from "@/components/ui/forms/TextInput";
import { FilteredList } from "@/components/ui/lists/FilteredList";
import { useOrganizations } from "@/hooks/data";
import { getServiceName, ServiceIdValues } from "@/services";
import type {
  IOrganization,
  IOrganizationInput,
  IServiceData,
} from "@/services";
import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import layoutStyles from "@/components/admin/AdminSectionLayout.module.css";
import styles from "./OrganizationsSection.module.css";
import type { OrganizationsSectionProps } from "./types";
import SectionHeader from "@/components/layout/SectionHeader";

/**
 * OrganizationsSection - Manage organizations
 * Part of the Superadmin page
 */
export const OrganizationsSection: React.FC<OrganizationsSectionProps> = ({
  className,
  "data-testid": testId = "organizations-section",
}) => {
  const {
    activeOrganizations,
    inactiveOrganizations,
    isLoading,
    error,
    saveOrganization,
    activateOrganization,
    deactivateOrganization,
    isSaving,
    isTogglingActivation,
  } = useOrganizations();

  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    tenant_id: string;
    service_id: string;
    service_data: IServiceData;
  }>({
    name: "",
    tenant_id: "",
    service_id: "",
    service_data: {
      token: "",
      app_key: "",
      client_key: "",
      subscription_key: "",
    },
  });

  // Flash alerts
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Service options for Select
  const serviceOptions: SelectOption<string>[] = useMemo(
    () => [
      {
        id: ServiceIdValues.XLEDGER,
        label: "Xledger",
        data: ServiceIdValues.XLEDGER,
      },
      {
        id: ServiceIdValues.POWEROFFICE,
        label: "PowerOffice",
        data: ServiceIdValues.POWEROFFICE,
      },
    ],
    [],
  );

  // Selected service for Select component
  const selectedService = useMemo(() => {
    if (!formData.service_id) return [];
    const option = serviceOptions.find((o) => o.id === formData.service_id);
    return option ? [option] : [];
  }, [formData.service_id, serviceOptions]);

  // Handle form field changes
  const handleFieldChange = useCallback(
    (field: keyof typeof formData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  // Handle service data field changes
  const handleServiceDataChange = useCallback(
    (field: keyof IServiceData, value: string) => {
      setFormData((prev) => ({
        ...prev,
        service_data: { ...prev.service_data, [field]: value },
      }));
    },
    [],
  );

  // Handle service type change (for Select component)
  const handleServiceChange = useCallback(
    (selected: SelectOption<string>[]) => {
      const value = selected[0]?.data ?? "";
      setFormData((prev) => ({
        ...prev,
        service_id: value,
        service_data: {
          token: "",
          app_key: "",
          client_key: "",
          subscription_key: "",
        },
      }));
    },
    [],
  );

  // Validate form
  const validateForm = useCallback((): string | null => {
    if (!formData.name.trim()) {
      return "Navn er påkrevd";
    }
    if (!formData.tenant_id.trim()) {
      return "Tenant ID er påkrevd";
    }
    if (!formData.service_id) {
      return "Tjeneste er påkrevd";
    }
    if (formData.service_id === ServiceIdValues.XLEDGER) {
      if (!formData.service_data.token?.trim()) {
        return "Token er påkrevd for Xledger";
      }
    }
    if (formData.service_id === ServiceIdValues.POWEROFFICE) {
      if (!formData.service_data.app_key?.trim()) {
        return "App Key er påkrevd for PowerOffice";
      }
      if (!formData.service_data.client_key?.trim()) {
        return "Client Key er påkrevd for PowerOffice";
      }
      if (!formData.service_data.subscription_key?.trim()) {
        return "Subscription Key er påkrevd for PowerOffice";
      }
    }
    return null;
  }, [formData]);

  // Handle form submit
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const validationError = validateForm();
      if (validationError) {
        setErrorMessage(validationError);
        return;
      }

      const payload: IOrganizationInput = {
        name: formData.name.trim(),
        tenant_id: formData.tenant_id.trim(),
        service_id: formData.service_id,
        service_data: formData.service_data,
      };

      const success = await saveOrganization(payload);

      if (success) {
        setSuccessMessage("Organisasjon lagret");
        // Reset form
        setFormData({
          name: "",
          tenant_id: "",
          service_id: "",
          service_data: {
            token: "",
            app_key: "",
            client_key: "",
            subscription_key: "",
          },
        });
      } else {
        setErrorMessage("Kunne ikke lagre organisasjon");
      }
    },
    [formData, validateForm, saveOrganization],
  );

  // Handle activate
  const handleActivate = useCallback(
    async (org: IOrganization) => {
      const success = await activateOrganization(org.id);
      if (success) {
        setSuccessMessage(`${org.tenant_name} er aktivert`);
      } else {
        setErrorMessage(`Kunne ikke aktivere ${org.tenant_name}`);
      }
    },
    [activateOrganization],
  );

  // Handle deactivate
  const handleDeactivate = useCallback(
    async (org: IOrganization) => {
      const success = await deactivateOrganization(org.id);
      if (success) {
        setSuccessMessage(`${org.tenant_name} er deaktivert`);
      } else {
        setErrorMessage(`Kunne ikke deaktivere ${org.tenant_name}`);
      }
    },
    [deactivateOrganization],
  );

  // Render organization item
  const renderOrgItem = useCallback(
    (org: IOrganization, isActive: boolean) => (
      <div
        key={org.id}
        className={styles.orgItem}
        data-testid={`${testId}-org-${org.id}`}
      >
        <div className={styles.orgInfo}>
          <div className={styles.orgName}>{org.tenant_name}</div>
          <div className={styles.orgDetails}>
            <span className={styles.tenantId}>{org.tenant_id}</span>
            <Badge variant="info" size="sm">
              {getServiceName(org.service_id)}
            </Badge>
          </div>
        </div>
        <Button
          onClick={() =>
            isActive ? handleDeactivate(org) : handleActivate(org)
          }
          disabled={isTogglingActivation}
          variant="outline"
          size="sm"
          className={classNames(
            styles.actionButton,
            isActive ? styles.deactivateButton : styles.activateButton,
          )}
          data-testid={`${testId}-toggle-${org.id}`}
        >
          {isActive ? "Deaktiver" : "Aktiver"}
        </Button>
      </div>
    ),
    [handleActivate, handleDeactivate, isTogglingActivation, testId],
  );

  return (
    <div
      className={classNames(layoutStyles.section, styles.section, className)}
      data-testid={testId}
    >
      {/* Explanation text - visible only on desktop */}
      <p className={layoutStyles.explanationText}>
        Opprett organisasjoner og koble dem til regnskapssystem (Xledger eller
        PowerOffice) for synkronisering av ansatte og timeføring.
      </p>

      {/* Flash alerts */}
      {successMessage && (
        <FlashAlert
          alert={{ message: successMessage, variant: "success" }}
          onDismiss={() => setSuccessMessage(null)}
          data-testid={`${testId}-success-alert`}
        />
      )}
      {errorMessage && (
        <FlashAlert
          alert={{ message: errorMessage, variant: "error" }}
          onDismiss={() => setErrorMessage(null)}
          data-testid={`${testId}-error-alert`}
        />
      )}

      <div className={layoutStyles.dataAreaGrid}>
        {/* Organization form */}
        <section className={layoutStyles.card} data-area="org-form">
          <SectionHeader as="h2" size="md">
            Opprett/oppdater organisasjon
          </SectionHeader>
          <form
            className={styles.form}
            onSubmit={handleSubmit}
            data-testid={`${testId}-form`}
          >
            <div className={styles.formRow}>
              <FormField label="Tenant ID" htmlFor="org-tenant-id">
                <TextInput
                  id="org-tenant-id"
                  value={formData.tenant_id}
                  onChange={(value) => handleFieldChange("tenant_id", value)}
                  disabled={isSaving}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  fullWidth
                  data-testid={`${testId}-tenant-id`}
                />
              </FormField>
              <FormField label="Navn" htmlFor="org-name">
                <TextInput
                  id="org-name"
                  value={formData.name}
                  onChange={(value) => handleFieldChange("name", value)}
                  disabled={isSaving}
                  placeholder="Organisasjonsnavn"
                  fullWidth
                  data-testid={`${testId}-name`}
                />
              </FormField>
            </div>

            <FormField label="Tjeneste" htmlFor="org-service">
              <Select<string>
                appearance="clean"
                placeholder="Velg tjeneste..."
                options={serviceOptions}
                value={selectedService}
                onChange={handleServiceChange}
                disabled={isSaving}
                data-testid={`${testId}-service`}
              />
            </FormField>

            {/* Xledger fields */}
            {formData.service_id === ServiceIdValues.XLEDGER && (
              <div className={styles.serviceDataFields}>
                <div className={styles.serviceDataTitle}>
                  Xledger-innstillinger
                </div>
                <FormField label="Token" htmlFor="org-token">
                  <TextInput
                    id="org-token"
                    type="password"
                    value={formData.service_data.token || ""}
                    onChange={(value) =>
                      handleServiceDataChange("token", value)
                    }
                    disabled={isSaving}
                    placeholder="Xledger token"
                    fullWidth
                    data-testid={`${testId}-token`}
                  />
                </FormField>
              </div>
            )}

            {/* PowerOffice fields */}
            {formData.service_id === ServiceIdValues.POWEROFFICE && (
              <div className={styles.serviceDataFields}>
                <div className={styles.serviceDataTitle}>
                  PowerOffice-innstillinger
                </div>
                <FormField label="App Key" htmlFor="org-app-key">
                  <TextInput
                    id="org-app-key"
                    type="password"
                    value={formData.service_data.app_key || ""}
                    onChange={(value) =>
                      handleServiceDataChange("app_key", value)
                    }
                    disabled={isSaving}
                    placeholder="App Key"
                    fullWidth
                    data-testid={`${testId}-app-key`}
                  />
                </FormField>
                <FormField label="Client Key" htmlFor="org-client-key">
                  <TextInput
                    id="org-client-key"
                    type="password"
                    value={formData.service_data.client_key || ""}
                    onChange={(value) =>
                      handleServiceDataChange("client_key", value)
                    }
                    disabled={isSaving}
                    placeholder="Client Key"
                    fullWidth
                    data-testid={`${testId}-client-key`}
                  />
                </FormField>
                <FormField
                  label="Subscription Key"
                  htmlFor="org-subscription-key"
                >
                  <TextInput
                    id="org-subscription-key"
                    type="password"
                    value={formData.service_data.subscription_key || ""}
                    onChange={(value) =>
                      handleServiceDataChange("subscription_key", value)
                    }
                    disabled={isSaving}
                    placeholder="Subscription Key"
                    fullWidth
                    data-testid={`${testId}-subscription-key`}
                  />
                </FormField>
              </div>
            )}

            <div className={styles.buttonRow}>
              <Button
                type="submit"
                disabled={isSaving}
                isLoading={isSaving}
                loadingText="Lagrer..."
                className={styles.submitButton}
                data-testid={`${testId}-submit`}
              >
                Opprett organisasjon
              </Button>
            </div>
          </form>

          <StatusMessages
            error={error}
            isLoading={isLoading}
            errorLabel="Kunne ikke laste organisasjoner"
            loadingLabel="Laster organisasjoner..."
            testId={testId}
          />
        </section>

        {/* Organization lists */}
        <section className={layoutStyles.card} data-area="org-lists">
          {!isLoading && !error && (
            <div className={styles.listsContainer}>
              {/* Active organizations */}
              <div className={styles.listSection}>
                <SectionHeader as="h2" size="md">
                  Aktive organisasjoner
                </SectionHeader>
                <FilteredList
                  items={activeOrganizations}
                  filterItem={(org, normalized) => {
                    const searchFields = [org.tenant_name, org.tenant_id];
                    return searchFields.some((value) =>
                      value.toLowerCase().includes(normalized),
                    );
                  }}
                  renderItem={(org) => renderOrgItem(org, true)}
                  placeholder="Søk etter navn eller tenant ID"
                  debounce={150}
                  searchDisabled={isLoading}
                  className={styles.listWrapper}
                  emptyState={
                    <div className={styles.emptyState}>
                      Ingen aktive organisasjoner
                    </div>
                  }
                  data-testid={`${testId}-active-list`}
                />
              </div>

              {/* Inactive organizations */}
              <div className={styles.listSection}>
                <SectionHeader as="h2" size="md">
                  Inaktive organisasjoner
                </SectionHeader>
                <FilteredList
                  items={inactiveOrganizations}
                  filterItem={(org, normalized) => {
                    const searchFields = [org.tenant_name, org.tenant_id];
                    return searchFields.some((value) =>
                      value.toLowerCase().includes(normalized),
                    );
                  }}
                  renderItem={(org) => renderOrgItem(org, false)}
                  placeholder="Søk etter navn eller tenant ID"
                  debounce={150}
                  searchDisabled={isLoading}
                  className={styles.listWrapper}
                  emptyState={
                    <div className={styles.emptyState}>
                      Ingen inaktive organisasjoner
                    </div>
                  }
                  data-testid={`${testId}-inactive-list`}
                />
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

OrganizationsSection.displayName = "OrganizationsSection";

export default OrganizationsSection;
